import { useState, useEffect } from 'react';
import { Activity, Clock, TrendingUp, TrendingDown, RefreshCw, Zap, AlertCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API_URL = '';

const COLORS = {
  'Login': '#0ea5e9',
  'Verificación Masiva': '#f59e0b',
  'Crear Factura': '#10b981',
  'Listar Facturas': '#8b5cf6',
  'Parsing Stats': '#ec4899',
  'Estado Caché': '#6366f1',
  'Extracciones Email': '#14b8a6',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const hour = label?.slice(11, 13) || '';
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
      <p className="font-semibold text-gray-700 mb-2">{hour}:00h</p>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center justify-between gap-4">
          <span style={{ color: entry.color }} className="font-medium">{entry.name}</span>
          <span className="text-gray-800 font-semibold">{entry.value}ms</span>
        </div>
      ))}
    </div>
  );
};

const SummaryCard = ({ label, stats }) => {
  const isGood = stats.avg_ms < 1000;
  const isWarning = stats.avg_ms >= 1000 && stats.avg_ms < 3000;
  const bgColor = isGood ? 'from-green-50 to-emerald-50' : isWarning ? 'from-amber-50 to-yellow-50' : 'from-red-50 to-rose-50';
  const textColor = isGood ? 'text-green-700' : isWarning ? 'text-amber-700' : 'text-red-700';
  const iconColor = isGood ? 'text-green-500' : isWarning ? 'text-amber-500' : 'text-red-500';

  return (
    <div className={`bg-gradient-to-br ${bgColor} rounded-lg p-3 border border-gray-100`} data-testid={`perf-summary-${label.replace(/\s/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-gray-600 truncate">{label}</span>
        {isGood ? <Zap className={`w-3.5 h-3.5 ${iconColor}`} /> : <AlertCircle className={`w-3.5 h-3.5 ${iconColor}`} />}
      </div>
      <div className={`text-xl font-bold ${textColor}`}>{stats.avg_ms}ms</div>
      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
        <span>Max: {stats.max_ms}ms</span>
        <span className="text-gray-300">|</span>
        <span>{stats.count} req</span>
      </div>
    </div>
  );
};

export const PerformanceHistoryPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/performance-history`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar datos');
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Format hour labels for the chart
  const formatHour = (tick) => {
    if (!tick) return '';
    return tick.slice(11, 13) + ':00';
  };

  if (loading && !data) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="performance-history-panel">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Historial de Rendimiento</h3>
        </div>
        <div className="flex flex-col items-center justify-center py-8">
          <RefreshCw className="w-8 h-8 text-cyan-500 animate-spin mb-3" />
          <span className="text-gray-600">Cargando métricas...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="performance-history-panel">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Historial de Rendimiento</h3>
        </div>
        <div className="text-center py-6 text-gray-500">
          <p>No hay datos de rendimiento disponibles aún.</p>
          <p className="text-sm mt-1">Los datos se recolectan automáticamente con el uso del sistema.</p>
        </div>
      </div>
    );
  }

  const hasDataPoints = data?.data_points?.length > 0;
  const labels = data?.labels || [];
  const summary = data?.summary || {};

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="performance-history-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-cyan-600" />
          <h3 className="font-semibold text-gray-800">Historial de Rendimiento</h3>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">Últimas 24h</span>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          title="Actualizar"
          data-testid="perf-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Summary Cards */}
      {Object.keys(summary).length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
          {Object.entries(summary).map(([label, stats]) => (
            <SummaryCard key={label} label={label} stats={stats} />
          ))}
        </div>
      )}

      {/* Chart */}
      {hasDataPoints ? (
        <div className="h-64" data-testid="perf-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.data_points} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="hour"
                tickFormatter={formatHour}
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#9ca3af' }}
                axisLine={{ stroke: '#e5e7eb' }}
                label={{ value: 'ms', position: 'insideTopLeft', offset: -5, style: { fontSize: 11, fill: '#9ca3af' } }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="circle"
                iconSize={8}
              />
              {labels.map((label) => (
                <Line
                  key={label}
                  type="monotone"
                  dataKey={label}
                  stroke={COLORS[label] || '#64748b'}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>No hay datos de rendimiento aún.</p>
          <p className="text-sm mt-1">Los datos aparecerán a medida que uses el sistema.</p>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="flex flex-wrap gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span>&lt;1s Excelente</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span>1-3s Revisar</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span>&gt;3s Problema</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceHistoryPanel;
